/**
 * EL FILTRO DEL LIBRO POR IDENTIFICADOR DE ASIENTO — el contrato que comparten
 * las dos puertas.
 *
 * POR QUÉ EXISTE. Un informe fiscal bloqueado por la validación previa (p. ej.
 * seis asientos sin precio) nombraba los asientos por su UUID y mandaba al
 * usuario a «Transacciones», donde nada podía acotar la rejilla a esos seis: ni
 * clave de URL, ni filtro de columna, ni parámetro de la API. Con 1.866 filas en
 * el libro, un UUID que no se puede abrir es un callejón sin salida.
 *
 * El backend declara `ids` en TODAS las puertas que aceptan los filtros del
 * libro (listado, cursor, contador, borrado por filtro); el frontend lo lleva en
 * la URL como `filter_ids`. Ambos leen la lista con ESTA función, de modo que
 * «qué ids pidió el usuario» no puede significar dos cosas distintas a cada lado.
 */

/**
 * Cuántos identificadores admite un filtro. Un tope, no una sugerencia: el
 * backend responde 400 por encima, en vez de recortar en silencio (un recorte
 * silencioso en un borrado por filtro destruiría un conjunto que nadie pidió).
 */
export const LEDGER_IDS_FILTER_MAX = 500

/**
 * Cuántos identificadores admite UNA consulta de existencia (`POST /transactions/existing-ids`). El cuerpo de una
 * petición no tiene el límite de 8 KB de una URL (nginx): el tope es el de la base de datos, no el de la red — 5.000
 * ids son unos 195 KB (el 2 % del `bodyLimit` de 10 MB) y 5.000 parámetros de `IN` (Postgres admite 65.535).
 */
export const LEDGER_IDS_EXISTENCE_MAX = 5000

/** Separador en la forma de texto (`?ids=a,b,c`, `?filter_ids=a,b,c`). */
export const LEDGER_IDS_FILTER_SEPARATOR = ','

/**
 * Forma canónica de un UUID tal y como la columna `uuid` de PostgreSQL la
 * acepta: 8-4-4-4-12 hexadecimales, sin llaves. No se exige versión ni variante
 * RFC: la columna tampoco lo hace, y un id válido para la base de datos no puede
 * ser rechazado por la puerta.
 */
export const LEDGER_TRANSACTION_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Lee la lista tal y como llega: una cadena separada por comas, una lista de
 * cadenas (`?ids=a&ids=b`) o una mezcla. Recorta espacios, descarta huecos y
 * elimina duplicados sin distinguir mayúsculas (PostgreSQL compara los `uuid`
 * así), conservando el orden de la primera aparición. NO valida la forma: eso lo
 * decide quien llama (el backend responde 400; el frontend descarta).
 */
export function parseLedgerIdsFilter(raw: string | readonly string[]): string[] {
  const pieces = typeof raw === 'string' ? [raw] : raw
  const seen = new Set<string>()
  const ids: string[] = []
  for (const piece of pieces) {
    for (const token of String(piece).split(LEDGER_IDS_FILTER_SEPARATOR)) {
      const id = token.trim().toLowerCase()
      if (id === '' || seen.has(id)) continue
      seen.add(id)
      ids.push(id)
    }
  }
  return ids
}

/** `true` cuando `value` tiene la forma de un id de asiento. */
export function isLedgerTransactionId(value: string): boolean {
  return LEDGER_TRANSACTION_ID_PATTERN.test(value)
}
